import { Wallet } from 'thirdweb/wallets'
import { axiosClient } from '.'
import { signMessage } from 'thirdweb/utils'
import { initSafeClient } from '../safe-protocol-kit/safe-client'
import { NetworkId } from 'src/common/consts'
import { AxiosResponse } from 'axios'

export enum Tier {
  tier0 = 'tier0',
  tier1 = 'tier1',
  tier2 = 'tier2',
  tier90 = 'tier90',
  tier99 = 'tier99',
  admin = 'admin',
}
export interface GetTierResponse {
  attributes: {
    tier: Tier
  }
}
export const getTier = async (address: string = ''): Promise<GetTierResponse> =>
  axiosClient.get(`addresses/${address}/tier`).then(data => data?.data?.data)

let accessToken: string = ''
export const register = async (address: string, signedMessage: string) => {
  const networks = [
    NetworkId.Polygon,
    NetworkId.Ethereum,
    NetworkId.Base,
    NetworkId.ArbitrumSepolia,
  ]
  const safes = await Promise.all(
    networks.map(() => initSafeClient(address, 137))
  )
  const safeAddresses = await Promise.all(safes.map(safe => safe?.getAddress()))
  const safeAddressesByNetworks = networks.reduce<Record<string, string>>(
    (acc, network, idx) => ({
      ...acc,
      [network]: safeAddresses[idx],
    }),
    {}
  )
  const response = await axiosClient.post(`register`, {
    data: {
      type: 'register',
      attributes: {
        auth_pair: {
          address,
          signed_message: signedMessage,
        },
        safe_addresses: safeAddressesByNetworks,
      },
    },
  })
  accessToken = response.data.data.attributes.access_token
  return response
}

export const login = async (address: string, signedMessage: string) => {
  const response = await axiosClient.post(`login`, {
    data: {
      type: 'login_request',
      attributes: {
        auth_pair: {
          address,
          signed_message: signedMessage,
        },
      },
    },
  })
  accessToken = response.data.data.attributes.access_token
  return response
}

export const getProfile = async (): Promise<any> =>
  axiosClient
    .get(`profile`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then(data => data?.data?.data)

export const updateSafeAddresses = async (
  accountAddress: string,
  safeAddress: string
) => {
  await axiosClient.patch(
    `addresses/${accountAddress}/safe-addresses`,
    {
      data: {
        attributes: {
          safe_addresses: {
            137: safeAddress,
            421614: safeAddress,
            1: safeAddress,
            8453: safeAddress,
          },
        },
      },
    },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
}

export const isTier2 = async (): Promise<boolean | null> => {
  if (!accessToken) return null
  try {
    const profileRes = await getProfile()
    return profileRes?.attributes?.tier === 'tier2'
  } catch {
    return false
  }
}

export const nonceMessage = async (address: string, termsHash?: string) => {
  const data = await axiosClient.post(`nonce`, {
    data: {
      type: 'auth_nonce_request',
      attributes: {
        address,
        terms_hash: termsHash,
      },
    },
  })

  return data
}

export const signupWithWallet = async (
  wallet: Wallet
): Promise<{
  new: boolean
  kycNotStarted: boolean
}> => {
  const account = wallet.getAccount()
  if (account?.address) {
    const messageData = await nonceMessage(
      account.address,
      'Terms and Conditions'
    )
    const signedMessage = await signMessage({
      message: messageData.data.data.attributes.message,
      account,
    })

    const tier = await getTier(account.address)
    if (!tier?.attributes?.tier) {
      await register(account.address, signedMessage)
    } else {
      await login(account.address, signedMessage)
    }
    return {
      new: !tier?.attributes?.tier || tier?.attributes?.tier === Tier.tier0,
      kycNotStarted: tier?.attributes?.tier === Tier.tier0,
    }
  }
  return { new: false, kycNotStarted: false }
}

export const createSumsubKYCSession = (
  sourceKey: string
): Promise<{
  data: {
    id: string
    type: 'sumsub_access_token'
    attributes: { token: string }
  }
}> =>
  axiosClient
    .post(
      `sumsub/kyc/sessions?sourceKey=${sourceKey}&level_name=Carbon / Biochar KYC`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    .then(data => data.data)

export const getSumsubKYCSessionAccessToken = (
  sourceKey: string
): Promise<string> =>
  axiosClient
    .get(
      `sumsub/kyc/access-token?sourceKey=${sourceKey}&level_name=Carbon / Biochar KYC`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    .then(
      (data: AxiosResponse<{ attributes: { token: string } }>) =>
        data.data.attributes.token
    )
